'use client';

import React, { useRef } from 'react';
import { Check } from 'lucide-react';
import {
  type Document,
  DocumentType as DocumentTypeEnum,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { HighlightText } from '@paeffgen/react/crm-documents/ui';
import { formatDate, formatCurrency, TYPE_BADGES, TYPE_SHORT } from '@paeffgen/react/crm-documents/util';
import { clsx } from 'clsx';

import { DocumentSearchContextLines } from './document-search-context';

interface DocumentRowProps {
  document: Document;
  isSelected?: boolean;
  /** True when at least one document is selected anywhere in the list */
  selectionMode?: boolean;
  onToggleSelection?: (id: string) => void;
  /** Open the document detail dialog */
  onOpen: () => void;
}

export const DocumentRow = React.memo(
  function DocumentRow({
    document,
    isSelected = false,
    selectionMode = false,
    onToggleSelection,
    onOpen,
  }: DocumentRowProps) {
    const getMobileBorderColor = (type: DocumentTypeEnum) => {
      switch (type) {
        case DocumentTypeEnum.ANGEBOT:
          return 'border-l-status-offer';
        case DocumentTypeEnum.AUFTRAG:
          return 'border-l-status-order';
        case DocumentTypeEnum.LIEFERSCHEIN:
          return 'border-l-status-delivery';
        case DocumentTypeEnum.RECHNUNG:
          return 'border-l-status-invoice';
        case DocumentTypeEnum.GUTSCHRIFT:
          return 'border-l-status-credit';
        default:
          return 'border-l-zinc-300';
      }
    };

    const rowRef = useRef<HTMLDivElement>(null);

    // Ungelesen-Markierung (Posteingang-Modell). isRead === false → ungelesen;
    // null/true → als gelesen behandeln (z.B. Pfade ohne aufgelösten Status).
    const isUnread = document.isRead === false;

    const handleSelectOrOpen = () => {
      if (selectionMode) {
        onToggleSelection?.(document.id);
      } else {
        onOpen();
      }
    };

    // Long-press to enter selection mode (mobile)
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressFired = useRef(false);

    const handlePointerDown = () => {
      longPressFired.current = false;
      longPressTimer.current = setTimeout(() => {
        longPressFired.current = true;
        onToggleSelection?.(document.id);
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate(15);
        }
      }, 450);
    };
    const cancelLongPress = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };
    const handleClickGuarded = () => {
      if (longPressFired.current) {
        longPressFired.current = false;
        return;
      }
      handleSelectOrOpen();
    };

    return (
      <div
        ref={rowRef}
        data-selected={isSelected || undefined}
        // Klick sitzt auf der WURZEL, nicht auf den beiden inneren Blöcken:
        // so öffnet auch der Positionsbereich den Beleg (Bubbling). Die
        // Badge-/Checkbox-Buttons rufen stopPropagation und bleiben unberührt.
        onClick={handleClickGuarded}
        className={clsx(
          'transition-colors scroll-mt-24 cursor-pointer',
          'mb-2 last:mb-0 rounded-xl border border-l-4 bg-white',
          isSelected
            ? 'border-primary border-l-primary bg-orange-50/40'
            : clsx('border-gray-200', getMobileBorderColor(document.type)),
          'md:mb-0 md:rounded-none md:border-0 md:border-b md:border-gray-100 md:last:border-b-0 md:border-l-4',
          isSelected
            ? 'md:bg-orange-50/40 md:border-l-primary'
            : 'md:border-l-transparent md:hover:bg-gray-50/50'
        )}
      >
        {/* Mobile Card View (< md) */}
        <div
          onPointerDown={handlePointerDown}
          onPointerUp={cancelLongPress}
          onPointerLeave={cancelLongPress}
          onPointerCancel={cancelLongPress}
          className="md:hidden p-3 transition-colors relative flex items-center gap-3 select-none"
        >
          {/* Type badge OR selection checkbox (replaces badge in selection mode) */}
          {selectionMode ? (
            <span
              className={clsx(
                'w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 transition-colors',
                isSelected
                  ? 'bg-primary border-primary text-white'
                  : 'bg-white border-gray-300 text-transparent',
              )}
              aria-hidden="true"
            >
              <Check size={16} strokeWidth={3} />
            </span>
          ) : (
            <div
              className={clsx(
                'w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-bold shrink-0',
                TYPE_BADGES[document.type],
              )}
            >
              {TYPE_SHORT[document.type]}
            </div>
          )}

          {/* Body: number + date, optional commission */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              {isUnread && (
                <span
                  className="w-2 h-2 rounded-full bg-primary shrink-0 self-center"
                  aria-label="Ungelesen"
                  title="Ungelesen"
                />
              )}
              <span
                className={clsx(
                  'text-sm tabular-nums truncate',
                  isUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-500',
                )}
              >
                <HighlightText text={document.documentNumber} />
              </span>
              <span className="text-xs text-gray-500 tabular-nums shrink-0">
                {formatDate(document.documentDate)}
              </span>
            </div>
            {(document.commission || document.yourReference) && (
              <div className="text-xs text-gray-600 truncate mt-0.5">
                <HighlightText
                  text={document.commission ?? document.yourReference ?? ''}
                />
              </div>
            )}
          </div>

          {/* Right: amount (PDF-Aktionen leben im Beleg-Modal) */}
          <div className="flex items-center gap-2 shrink-0">
            {document.type !== DocumentTypeEnum.LIEFERSCHEIN &&
              document.netTotal != null && (
                <span
                  className={clsx(
                    'text-sm font-bold tabular-nums',
                    document.type === DocumentTypeEnum.GUTSCHRIFT
                      ? 'text-red-600'
                      : 'text-gray-900',
                  )}
                >
                  {formatCurrency(document.netTotal)}
                </span>
              )}
          </div>
        </div>

        {/* Desktop Grid View (>= md) */}
        <div
          className={clsx(
            'hidden md:grid gap-4 p-4 items-center transition-colors group grid-cols-1',
            isSelected
              ? 'bg-orange-50/40 hover:bg-orange-50/60'
              : 'hover:bg-gray-50/50'
          )}
        >
          <div className="grid grid-cols-12 gap-4 w-full items-center">
            {/* Type & Number — badge becomes checkbox in selection mode */}
            <div className="col-span-3 flex items-center gap-3">
              {selectionMode ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelection?.(document.id);
                  }}
                  aria-label={isSelected ? 'Auswahl entfernen' : 'Auswählen'}
                  className={clsx(
                    'w-8 h-8 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors',
                    isSelected
                      ? 'bg-primary border-primary text-white'
                      : 'bg-white border-gray-300 text-transparent hover:border-primary',
                  )}
                >
                  <Check size={16} strokeWidth={3} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelection?.(document.id);
                  }}
                  aria-label="Beleg auswählen"
                  className={clsx(
                    'px-3 py-1 rounded-md text-sm font-bold border uppercase tracking-wide hover:opacity-80 transition-opacity',
                    TYPE_BADGES[document.type]
                  )}
                >
                  {document.type.charAt(0)}
                </button>
              )}
              <span className="flex items-center gap-2 min-w-0">
                {isUnread && (
                  <span
                    className="w-2 h-2 rounded-full bg-primary shrink-0"
                    aria-label="Ungelesen"
                    title="Ungelesen"
                  />
                )}
                <span
                  className={clsx(
                    'text-sm truncate',
                    isUnread ? 'font-bold text-gray-900' : 'font-normal text-gray-500',
                  )}
                >
                  <HighlightText text={document.documentNumber} />
                </span>
              </span>
            </div>

            {/* Date */}
            <div className="col-span-2 text-sm text-gray-600">
              {formatDate(document.documentDate)}
            </div>

            {/* Reference / Commission */}
            <div className="col-span-4">
              <p
                className="text-sm font-medium text-gray-900 truncate"
                title={document.commission ?? undefined}
              >
                {document.commission ? (
                  <HighlightText text={document.commission} />
                ) : (
                  '-'
                )}
              </p>
              {document.yourReference && (
                <p className="text-sm text-gray-600 truncate">
                  Ref: <HighlightText text={document.yourReference} />
                </p>
              )}
            </div>

            {/* Amount — not shown for Lieferscheine */}
            <div className="col-span-3 text-right">
              {document.type !== DocumentTypeEnum.LIEFERSCHEIN && (
                <span
                  className={clsx(
                    'font-bold tabular-nums text-sm',
                    document.type === DocumentTypeEnum.GUTSCHRIFT
                      ? 'text-red-600'
                      : 'text-gray-900'
                  )}
                >
                  {document.type === DocumentTypeEnum.GUTSCHRIFT ? '-' : ''}
                  {formatCurrency(document.netTotal ?? 0)}
                </span>
              )}
            </div>
          </div>
        </div>

        <DocumentSearchContextLines context={document.searchContext} />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.document.id === nextProps.document.id &&
      prevProps.document.isRead === nextProps.document.isRead &&
      // Ohne diesen Vergleich bliebe die Zeile beim Zuschalten der
      // Positionsansicht unverändert stehen, obwohl neue Daten da sind.
      prevProps.document.searchContext === nextProps.document.searchContext &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.selectionMode === nextProps.selectionMode
    );
  }
);
